import React, { memo } from 'react';
import { GoalsConfidenceIndicator } from './GoalsConfidenceIndicator';
import type { GoalsFixtureSummary } from '../../../types';

interface GoalsFixtureCardProps {
  fixture: GoalsFixtureSummary;
  onSelect: () => void;
}

function GoalsFixtureCardComponent({ fixture, onSelect }: GoalsFixtureCardProps) {
  const formatTime = (kickoff: string | null) => {
    if (!kickoff) return '--:--';
    const date = new Date(kickoff);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const formatProb = (prob: number | null) => {
    if (prob === null) return '--%';
    return `${(prob * 100).toFixed(0)}%`;
  };

  return (
    <div className="goals-fixture-card" onClick={onSelect}>
      {/* Team Row */}
      <div className="goals-fixture-teams">
        <div className="goals-team">
          {fixture.home_team.logo && (
            <img src={fixture.home_team.logo} alt="" className="goals-team-logo" />
          )}
          <span className="goals-team-name">{fixture.home_team.name}</span>
        </div>
        <span className="goals-vs">vs</span>
        <div className="goals-team">
          {fixture.away_team.logo && (
            <img src={fixture.away_team.logo} alt="" className="goals-team-logo" />
          )}
          <span className="goals-team-name">{fixture.away_team.name}</span>
        </div>
      </div>

      {/* Kickoff & Context */}
      <div className="goals-fixture-meta">
        <span className="goals-kickoff">{formatTime(fixture.kickoff)}</span>
        {fixture.is_derby && <span className="goals-badge goals-badge-derby">Derby</span>}
        {fixture.is_title_race && <span className="goals-badge goals-badge-title">Title</span>}
        {fixture.is_relegation && <span className="goals-badge goals-badge-relegation">Rel.</span>}
      </div>

      {/* Probabilities */}
      <div className="goals-fixture-probs">
        <div className="goals-prob-item goals-prob-btts">
          <span className="goals-prob-label">BTTS</span>
          <span className="goals-prob-value">{formatProb(fixture.btts_yes_prob)}</span>
        </div>
        <div className="goals-prob-item goals-prob-o25">
          <span className="goals-prob-label">O2.5</span>
          <span className="goals-prob-value">{formatProb(fixture.over_2_5_prob)}</span>
        </div>
        <div className="goals-prob-item goals-prob-combo">
          <span className="goals-prob-label">BTTS+O2.5</span>
          <span className="goals-prob-value">{formatProb(fixture.btts_o2_5_prob)}</span>
        </div>
      </div>

      {/* Confidence & Insight */}
      <div className="goals-fixture-footer">
        {fixture.confidence_score && (
          <GoalsConfidenceIndicator
            score={fixture.confidence_score}
            level={fixture.confidence_level || 'moderate'}
            compact
          />
        )}
        {fixture.quick_insight && (
          <span className="goals-quick-insight">{fixture.quick_insight}</span>
        )}
      </div>

      {/* Saved indicator */}
      {fixture.is_saved && (
        <div className="goals-saved-indicator">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
          </svg>
        </div>
      )}
    </div>
  );
}

// Memoize to prevent unnecessary re-renders
export const GoalsFixtureCard = memo(GoalsFixtureCardComponent, (prev, next) => {
  return (
    prev.fixture.fixture_id === next.fixture.fixture_id &&
    prev.fixture.btts_yes_prob === next.fixture.btts_yes_prob &&
    prev.fixture.confidence_score === next.fixture.confidence_score &&
    prev.fixture.is_saved === next.fixture.is_saved
  );
});

export default GoalsFixtureCard;
